import axios from "axios";
import { API_URL } from "@/config";
import { useSubscribeForFile } from "@/utils/useSubscribeForFile";

const store = {
  namespaced: true,

  actions: {
    async getMany(_, criteria) {
      const response = await axios.get(`${API_URL}/inventory/ingredients`, {
        params: criteria,
      });

      if (!response.data || typeof response.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOne(_, payload) {
      // Accepts either a plain ingredientId (legacy calls) or { ingredientId, withPrices }.
      // withPrices is opt-in: the backend only returns purchase prices when explicitly
      // requested (and only to roles that are allowed to see them).
      const ingredientId = typeof payload === "object" ? payload.ingredientId : payload;
      const withPrices = typeof payload === "object" ? payload.withPrices : false;

      const response = await axios.get(`${API_URL}/inventory/ingredients/${ingredientId}`, {
        params: withPrices ? { withPrices: 1 } : {},
      });

      if (!response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async create(_, ingredientData) {
      const response = await axios.post(`${API_URL}/inventory/ingredients`, ingredientData);

      if (response.status !== 201 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async update(_, { ingredientId, ingredientData }) {
      const response = await axios.put(
        `${API_URL}/inventory/ingredients/${ingredientId}`,
        ingredientData,
      );

      if (response.status !== 200 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async delete(_, ingredientId) {
      const response = await axios.delete(`${API_URL}/inventory/ingredients/${ingredientId}`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async importIngredientsFromExcelFile(_, formData) {
      const response = await axios.post(
        `${API_URL}/inventory/ingredients/importIngredientsFromExcelFile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async ingredientsList({ rootGetters, commit }, reportData) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/ingredientsList", {
        url: `${API_URL}/reports/ingredientsList`,
        method: "POST",
        data: reportData,
      });
    },

    async ingredientOutcomes(_, criteria) {
      const response = await axios.get(`${API_URL}/inventory/ingredients/ingredientOutcomes`, {
        params: criteria,
      });

      if (!response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getPurchaseReport(_, criteria = {}) {
      const response = await axios.get(`${API_URL}/inventory/ingredients/purchaseReport`, {
        params: criteria,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getFoodCostReport(_, criteria) {
      const response = await axios.get(`${API_URL}/api/foodCostReport`, { params: criteria });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getProductionReport(_, criteria) {
      const response = await axios.get(`${API_URL}/api/productionReport`, { params: criteria });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOptions(_, criteria = {}) {
      const response = await axios.get(`${API_URL}/inventory/ingredients/options`, {
        params: criteria,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getStockLotsSummary(_, criteria) {
      const response = await axios.get(`${API_URL}/inventory/stockLots/summary`, {
        params: criteria,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },
  },
};

export default store;
